// Onglet « Corrections » côté bailleur (F-030) : demandes liées à un constat ou libres, suivi et résolution.
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Notice, Select, StatusChip, Textarea, fieldError, type CheckStatus } from "@core/ui";
import { APPLICATION_STATUS, ROLE_LABELS, docUrl, fullName, useAction, type Person } from "./shared";

type Correction = {
  id: string;
  applicant_id: string | null;
  applicant_name: string | null;
  applicant_role: string | null;
  check_id: string | null;
  check_title: string | null;
  document_category: string | null;
  title: string;
  message: string;
  status: "ouverte" | "resolue" | "annulee";
  candidate_reply: string | null;
  candidate_reply_at: string | null;
  resolution: string | null;
  resolution_note: string;
  resolved_document_id: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_by_name: string | null;
  created_at: string;
};
type Proposal = {
  check_id: string;
  applicant_id: string | null;
  document_category: string | null;
  status: CheckStatus;
  check_title: string;
  title: string;
  message: string;
  already_requested: boolean;
};
type View = {
  application: { id: string; status: string; household_label: string };
  items: Correction[];
  proposals: Proposal[];
  applicants: Person[];
  categories: { code: string; label: string }[];
};

export const CORRECTION_STATUS: Record<string, { label: string; status: CheckStatus }> = {
  ouverte: { label: "En attente du candidat", status: "a_confirmer" },
  resolue: { label: "Résolue", status: "conforme" },
  annulee: { label: "Annulée", status: "info" },
};

export function CorrectionsTab({ orgId, applicationId }: { orgId: string; applicationId: string }) {
  const org = useOrg();
  const base = `/api/v1/orgs/${orgId}/applications/${applicationId}`;
  const q = useGet<View>(`${base}/corrections`);
  const prefixes = [base, `/api/v1/orgs/${orgId}/decision-summary`];
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const canOperate = org.can("operate");
  const open = v.items.filter((i) => i.status === "ouverte");
  const closed = v.items.filter((i) => i.status !== "ouverte");
  const categoryLabel = (code: string | null) => v.categories.find((c) => c.code === code)?.label ?? code ?? "";
  return (
    <div className="stack">
      <Notice>
        Une demande précise ce qui a été observé et ce qui peut le résoudre. Tant qu’une demande est ouverte, la candidature est « À compléter » (statut actuel :{" "}
        <strong>{APPLICATION_STATUS[v.application.status] ?? v.application.status}</strong>). Une nouvelle pièce de la catégorie demandée résout la demande et rend obsolètes les seuls constats de l’ancienne version.
      </Notice>

      {canOperate ? (
        <Card title="Demandes proposées à partir des constats">
          {v.proposals.length ? (
            <div className="stack">
              {v.proposals.map((p) => (
                <ProposalItem key={p.check_id} p={p} base={base} prefixes={prefixes} categoryLabel={categoryLabel(p.document_category)} />
              ))}
            </div>
          ) : (
            <p className="muted">Aucun constat à confirmer, anomalie ou pièce manquante pour le moment.</p>
          )}
        </Card>
      ) : null}

      <Card title="Demandes en cours">
        {open.length ? (
          <div className="stack">
            {open.map((c) => (
              <CorrectionItem key={c.id} c={c} orgId={orgId} canOperate={canOperate} prefixes={prefixes} categoryLabel={categoryLabel(c.document_category)} />
            ))}
          </div>
        ) : (
          <Empty title="Aucune demande en cours">Le candidat n’a rien à compléter pour l’instant.</Empty>
        )}
      </Card>

      {canOperate ? <FreeRequest base={base} prefixes={prefixes} applicants={v.applicants} categories={v.categories} /> : null}

      {closed.length ? (
        <Card title="Historique des demandes">
          <div className="stack">
            {closed.map((c) => (
              <CorrectionItem key={c.id} c={c} orgId={orgId} canOperate={false} prefixes={prefixes} categoryLabel={categoryLabel(c.document_category)} />
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function ProposalItem({ p, base, prefixes, categoryLabel }: { p: Proposal; base: string; prefixes: string[]; categoryLabel: string }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(p.title);
  const [message, setMessage] = useState(p.message);
  const action = useAction(prefixes);
  return (
    <article className="decision-echange">
      <header className="row-between">
        <strong>{p.check_title}</strong>
        <StatusChip status={p.status} />
      </header>
      {categoryLabel ? <p className="small muted">Pièce concernée : {categoryLabel}</p> : null}
      {p.already_requested ? (
        <Badge tone="encre">Demande déjà envoyée</Badge>
      ) : editing ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run("envoyer", () => post(`${base}/corrections`, { check_id: p.check_id, title, message }), "Demande envoyée au candidat.");
            if (ok) setEditing(false);
          }}
        >
          <Input label="Objet" value={title} error={fieldError(action.error, "title")} onChange={(e) => setTitle(e.target.value)} />
          <Textarea label="Message au candidat" rows={8} value={message} error={fieldError(action.error, "message")} onChange={(e) => setMessage(e.target.value)} />
          <div className="form-actions">
            <Button type="submit" small busy={action.busy === "envoyer"}>
              Demander une correction
            </Button>
            <Button variant="discret" small onClick={() => setEditing(false)}>
              Annuler
            </Button>
          </div>
        </form>
      ) : (
        <div className="form-actions">
          <Button variant="secondaire" small onClick={() => setEditing(true)}>
            Préparer la demande
          </Button>
        </div>
      )}
    </article>
  );
}

function FreeRequest({ base, prefixes, applicants, categories }: { base: string; prefixes: string[]; applicants: Person[]; categories: { code: string; label: string }[] }) {
  const [form, setForm] = useState({ applicant_id: "", document_category: "", title: "", message: "" });
  const action = useAction(prefixes);
  return (
    <Card title="Demande libre">
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const ok = await action.run(
            "libre",
            () => post(`${base}/corrections`, { applicant_id: form.applicant_id || null, document_category: form.document_category || null, title: form.title, message: form.message }),
            "Demande envoyée au candidat.",
          );
          if (ok) setForm({ applicant_id: "", document_category: "", title: "", message: "" });
        }}
      >
        <div className="form-row">
          <Select
            label="Personne concernée"
            value={form.applicant_id}
            onChange={(e) => setForm({ ...form, applicant_id: e.target.value })}
            options={[{ value: "", label: "Tout le dossier" }, ...applicants.map((a) => ({ value: a.id, label: `${fullName(a)} (${ROLE_LABELS[a.role] ?? a.role})` }))]}
          />
          <Select
            label="Pièce attendue (facultatif)"
            help="Seules les pièces admissibles sont proposées."
            value={form.document_category}
            error={fieldError(action.error, "document_category")}
            onChange={(e) => setForm({ ...form, document_category: e.target.value })}
            options={[{ value: "", label: "Aucune pièce précise" }, ...categories.map((c) => ({ value: c.code, label: c.label }))]}
          />
        </div>
        <Input label="Objet" required value={form.title} error={fieldError(action.error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea
          label="Message au candidat"
          required
          help="Dites ce qui a été observé et ce qui peut le résoudre. Les pièces interdites par la loi (relevés bancaires, photo…) sont refusées."
          value={form.message}
          error={fieldError(action.error, "message")}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
        <div className="form-actions">
          <Button type="submit" busy={action.busy === "libre"}>
            Demander une correction
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CorrectionItem({ c, orgId, canOperate, prefixes, categoryLabel }: { c: Correction; orgId: string; canOperate: boolean; prefixes: string[]; categoryLabel: string }) {
  const [note, setNote] = useState("");
  const [closing, setClosing] = useState(false);
  const action = useAction(prefixes);
  const st = CORRECTION_STATUS[c.status];
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>{c.title}</strong>
          <span className="small muted">
            {" "}
            · {c.applicant_name ? `${c.applicant_name} (${ROLE_LABELS[c.applicant_role ?? ""] ?? ""})` : "Tout le dossier"} · demandée le {dateTime(c.created_at)}
            {c.created_by_name ? ` par ${c.created_by_name}` : ""}
          </span>
        </div>
        <StatusChip status={st.status} label={st.label} />
      </header>
      {c.check_title ? <p className="small muted">Constat lié : {c.check_title}</p> : null}
      {categoryLabel ? <p className="small muted">Pièce attendue : {categoryLabel}</p> : null}
      <details>
        <summary>Message envoyé</summary>
        <p className="message-body">{c.message}</p>
      </details>
      {c.candidate_reply ? (
        <div className="decision-reponse">
          <p className="small muted">Réponse du candidat le {dateTime(c.candidate_reply_at)}</p>
          <p className="message-body">{c.candidate_reply}</p>
        </div>
      ) : null}
      {c.status !== "ouverte" ? (
        <p className="small">
          {c.resolution === "nouvelle_piece" ? "Nouvelle pièce reçue" : c.resolution === "manuelle" ? "Résolue par l’espace" : "Annulée"} le {dateTime(c.resolved_at)}
          {c.resolved_by_name ? ` par ${c.resolved_by_name}` : ""}
          {c.resolution_note ? ` : ${c.resolution_note}` : ""}
          {c.resolved_document_id ? (
            <>
              {" "}
              ·{" "}
              <a href={docUrl(c.resolved_document_id)} target="_blank" rel="noreferrer">
                voir la pièce
              </a>
            </>
          ) : null}
        </p>
      ) : canOperate ? (
        closing ? (
          <form
            className="stack-sm"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await action.run("clore", () => post(`/api/v1/orgs/${orgId}/corrections/${c.id}/resolve`, { note }), "Demande close.");
              if (ok) setClosing(false);
            }}
          >
            <Textarea label="Comment le point a-t-il été résolu ?" help="La justification reste dans l’historique ; le constat d’origine n’est pas effacé." value={note} error={fieldError(action.error, "note")} onChange={(e) => setNote(e.target.value)} />
            <div className="form-actions">
              <Button type="submit" small busy={action.busy === "clore"}>
                Marquer comme résolue
              </Button>
              <Button variant="discret" small onClick={() => setClosing(false)}>
                Retour
              </Button>
            </div>
          </form>
        ) : (
          <div className="form-actions">
            <Button variant="secondaire" small onClick={() => setClosing(true)}>
              Clore la demande
            </Button>
            <Button variant="discret" small busy={action.busy === "annuler"} onClick={() => action.run("annuler", () => post(`/api/v1/orgs/${orgId}/corrections/${c.id}/cancel`, { note: "" }), "Demande annulée.")}>
              Annuler la demande
            </Button>
          </div>
        )
      ) : null}
    </article>
  );
}
